/**
 * Ticket Target Link Component
 * Displays a link to the reported content (target) with appropriate icon and route
 */

import { useState, useEffect } from 'react'
import { Box, Chip, Link as MuiLink, Tooltip, CircularProgress } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import {
  Map,
  Explore,
  LocationOn,
  Groups,
  Person,
  Forum,
  ChatBubble,
  Place,
  OpenInNew,
} from '@mui/icons-material'
import { TargetType, TargetTypeLabels } from '@api/types/tickets'
import { socialApi } from '@api/endpoints/social'

interface TicketTargetLinkProps {
  targetType: TargetType | null
  targetId: string | null
  variant?: 'chip' | 'link'
  showIcon?: boolean
}

const targetIcons: Record<TargetType, React.ReactNode> = {
  [TargetType.Cache]: <Map fontSize="small" />,
  [TargetType.Adventure]: <Explore fontSize="small" />,
  [TargetType.POI]: <Place fontSize="small" />,
  [TargetType.Location]: <LocationOn fontSize="small" />,
  [TargetType.Group]: <Groups fontSize="small" />,
  [TargetType.User]: <Person fontSize="small" />,
  [TargetType.Discussion]: <Forum fontSize="small" />,
  [TargetType.Post]: <ChatBubble fontSize="small" />,
}

// Map target types to their respective routes
// Returns [route, requiresAsyncData]
function getTargetRouteSync(targetType: TargetType, targetId: string): [string | null, boolean] {
  switch (targetType) {
    case TargetType.Cache:
      return [`/adventures/${targetId}`, false]
    case TargetType.Adventure:
      return [`/adventures/${targetId}`, false]
    case TargetType.POI:
      return [`/locations/${targetId}`, false]
    case TargetType.Location:
      return [`/locations/${targetId}`, false]
    case TargetType.Group:
      return [`/groups/${targetId}`, false]
    case TargetType.User:
      return [`/profile/${targetId}`, false]
    case TargetType.Discussion:
      // Needs to fetch discussion to get groupId
      return [null, true]
    case TargetType.Post:
      // Needs to fetch post and discussion to get groupId
      return [null, true]
    default:
      return [null, false]
  }
}

// Async function to build route for discussions and posts
async function getTargetRouteAsync(targetType: TargetType, targetId: string): Promise<string | null> {
  try {
    if (targetType === TargetType.Discussion) {
      // Fetch discussion to get groupId
      const discussion = await socialApi.getDiscussion(targetId)
      return `/groups/${discussion.groupId}?tab=discussions&discussionId=${targetId}`
    } else if (targetType === TargetType.Post) {
      // Fetch post to get discussionId, then fetch discussion to get groupId
      const post = await socialApi.getPost(targetId)
      const discussion = await socialApi.getDiscussion(post.discussionId)
      return `/groups/${discussion.groupId}?tab=discussions&discussionId=${post.discussionId}&postId=${targetId}`
    }
  } catch (error) {
    console.error('Failed to build target route:', error)
    return null
  }
  return null
}

export function TicketTargetLink({
  targetType,
  targetId,
  variant = 'chip',
  showIcon = true,
}: TicketTargetLinkProps) {
  const [route, setRoute] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // If no target, return null
  if (targetType === null || targetId === null) {
    return null
  }

  const label = TargetTypeLabels[targetType]
  const icon = targetIcons[targetType]

  // Determine if we need to fetch data to build the route
  const [syncRoute, requiresAsync] = getTargetRouteSync(targetType, targetId)

  // Fetch route asynchronously if needed
  useEffect(() => {
    if (requiresAsync && targetId) {
      setLoading(true)
      getTargetRouteAsync(targetType, targetId)
        .then((asyncRoute) => {
          setRoute(asyncRoute)
          setLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching target route:', error)
          setRoute(null)
          setLoading(false)
        })
    } else {
      setRoute(syncRoute)
    }
  }, [targetType, targetId, syncRoute, requiresAsync])

  if (variant === 'chip') {
    // Show loading spinner if we're fetching the route
    if (loading) {
      return (
        <Chip
          label={label}
          icon={showIcon ? <CircularProgress size={16} /> : undefined}
          size="small"
          sx={{ cursor: 'default' }}
        />
      )
    }

    return (
      <Tooltip title={route ? 'Click to view reported content' : 'Content link unavailable'} arrow>
        <Chip
          label={label}
          icon={showIcon ? <>{icon}</> : undefined}
          size="small"
          component={route ? RouterLink : 'div'}
          to={route || undefined}
          clickable={!!route}
          sx={{
            cursor: route ? 'pointer' : 'default',
            '&:hover': route
              ? {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                }
              : {},
          }}
        />
      </Tooltip>
    )
  }

  // Link variant
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        <CircularProgress size={16} />
        <span>{label}</span>
      </Box>
    )
  }

  if (!route) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        {showIcon && icon}
        <span>{label}</span>
      </Box>
    )
  }

  return (
    <MuiLink
      component={RouterLink}
      to={route}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {showIcon && icon}
      <span>{label}</span>
      <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
    </MuiLink>
  )
}
